import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { AttachmentReturn } from '../clients/rust-api-client';
import { loadMemberFileUrl } from '../Violations/comments/MemberAttachmentThumb';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

/** The first photo a browser can paint inline, if any. */
export const coverPhoto = (
  photos: AttachmentReturn[]
): AttachmentReturn | undefined =>
  photos.find(
    (p) =>
      p.renderable &&
      IMAGE_EXTENSIONS.indexOf(p.file_extension.toLowerCase()) !== -1
  );

interface Props {
  photo?: AttachmentReturn;
  alt: string;
  className?: string;
}

/**
 * An amenity's photo, served through the member file route (an `<img>`
 * cannot carry the bearer token, so it is fetched as a blob). Falls back
 * to a quiet placeholder rather than a broken image.
 */
const AmenityPhoto: React.FC<Props> = ({ photo, alt, className }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setUrl(null);
    setBroken(false);
    if (!photo) return;
    let cancelled = false;
    loadMemberFileUrl(photo.file_id)
      .then((next) => {
        if (!cancelled) setUrl(next);
      })
      .catch(() => {
        if (!cancelled) setBroken(true);
      });
    return () => {
      cancelled = true;
    };
  }, [photo]);

  if (photo && url && !broken) {
    return (
      <img
        src={url}
        alt={alt}
        className={clsx('w-full object-cover', className)}
      />
    );
  }

  return (
    <div
      className={clsx(
        'flex w-full items-center justify-center bg-zinc-100 dark:bg-zinc-800',
        photo && !broken && 'animate-pulse',
        className
      )}
      aria-hidden
    >
      <BuildingOffice2Icon className="h-10 w-10 text-zinc-400 dark:text-zinc-500" />
    </div>
  );
};

export default AmenityPhoto;
